<template>
  <layout-default
    :floatingButton="!loading && isAdmin"
    @floating-button-action="addNewPractice"
  >
    <template v-slot:toolbar>
      <practices-filters
        :value="queryParams.name"
        :isAdmin="isAdmin"
        @search-clear="searchClear"
        @search-on:input="searchInput"
        @search-on:run="searchRun"
        @filter-active="filterRun"
      />
    </template>
    <div class="practices">
      <v-overlay :value="loading" z-index="4">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>

      <!-- Generate Report Button -->
      <div v-if="isAdmin" class="practices-actions mb-4">
        <v-btn
          :disabled="!canGenerateReport"
          :loading="generateReportLoading"
          rounded
          elevation="0"
          color="accent"
          @click="generateCompaniesReport"
        >
          <v-icon left>bhi-download</v-icon>
          Generate Report
        </v-btn>
      </div>

      <main-user-table
        :headers="headers"
        :items="practicesWithSelection"
        :items-per-page="queryParams.pageSize"
        :server-items-length="pagination.totalCount || -1"
        :page="queryParams.page"
        :row-component="'practice-table-row-with-select'"
        :is-admin="isAdmin"
        @to-user="toPractice"
        @on-select="onSelectPractice"
        @action:get-data="getNewData"
      />
      <new-practice-dialog
        :show-dialog="showNewPracticeDialog"
        @close-dialog="closeNewPracticeDialog"
        @on-submit="onNewUserSubmit"
      />
    </div>
  </layout-default>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { debounce } from '@/helpers/debounce'
import LayoutDefault from '@/layouts/LayoutDefault.vue'
import PracticesFilters from '@/components/practices/PracticesFilters.vue'
import NewPracticeDialog from '@/components/dialogs/NewPracticeDialog.vue'
import MainUserTable from '@/components/tables/MainUserTable'
import RouterMixin from '@/mixins/router'

const defaultQueryParams = {
  sortBy: 'name',
  sortDirection: 'asc',
}

export default {
  name: 'Practices',
  components: {
    LayoutDefault,
    PracticesFilters,
    NewPracticeDialog,
    MainUserTable,
  },
  data() {
    return {
      avatar: 'https://via.placeholder.com/54',
      headers: [
        { sortable: false },
        { text: 'Name', value: 'name' },
        { text: 'Type', value: 'type' },
        { text: 'Practitioners No', value: 'practitionersNo' },
        { text: 'Participants No', value: 'participantsNo' },
        { text: 'Primary contact', value: 'contact', sortable: false },
        { text: 'Date added', value: 'creationDate' },
      ],
      showNewPracticeDialog: false,
      onlyActive: true,
      defaultQueryParams: { ...defaultQueryParams },
      selectedPractices: [], // Array to store selected practice IDs across all pages
    }
  },
  mixins: [RouterMixin],
  computed: {
    ...mapState('reportsGenerateStates', ['generateReportLoading']),
    ...mapState('practicesStates', ['practices', 'loading', 'pagination']),
    ...mapGetters('userStates', ['getRolename']),
    isAdmin() {
      return (
        this.dictionaryBhUsersRoleIds[this.getRolename] ===
        this.dictionaryBhUsersRoleIds.Admin
      )
    },
    ...mapState('dictionariesStates', ['dictionaryBhUsersRoleIds']),
    activeFilter() {
      if (this.isAdmin && this.onlyActive === null) return { isActive: null }
      return { isActive: true }
    },
    filteredQueryParams() {
      let obj = { ...this.defaultQueryParams, ...this.activeFilter }
      return Object.entries(obj).reduce(
        (a, [k, v]) => (v == null ? a : ((a[k] = v), a)),
        {},
      )
    },
    practicesWithSelection() {
      return this.practices.map(practice => ({
        ...practice,
        selected: this.selectedPractices.includes(practice.id),
      }))
    },
    canGenerateReport() {
      return this.selectedPractices.length > 0 && !this.generateReportLoading
    },
  },
  methods: {
    ...mapActions('practicesStates', ['getPractices', 'addPractice']),
    ...mapActions('reportsGenerateStates', ['generateReport']),
    getNewData(data) {
      this.getDataAction({ ...data, ...this.activeFilter })
    },
    toPractice(id) {
      this.$router.push({
        name: 'Practice',
        path: '/company/:id',
        params: { id: id },
      })
    },
    searchClear() {
      this.getDataAction({ ...this.defaultQueryParams, ...this.activeFilter })
    },
    searchInput({ key, value }) {
      this.getDataAction({
        ...this.defaultQueryParams,
        ...this.activeFilter,
        [key]: value,
      })
    },
    searchRun(search) {
      this.getDataAction({
        ...this.defaultQueryParams,
        ...this.activeFilter,
        name: search,
      })
    },
    addNewPractice() {
      this.showNewPracticeDialog = true
    },
    closeNewPracticeDialog() {
      this.showNewPracticeDialog = false
    },
    onNewUserSubmit(userData) {
      this.addPractice(userData)
    },
    getData(data) {
      if (data instanceof URLSearchParams) {
        data.set('isActive', this.activeFilter.isActive)
      } else if (data instanceof Object) {
        data = {
          ...data,
          ...this.activeFilter,
        }
      }

      debounce(
        () => {
          this.getPractices(data)
        },
        100,
        'getPatients',
      )
    },
    filterRun(val) {
      this.onlyActive = val

      this.getNewData({
        ...this.defaultQueryParams,
        ...this.queryParams,
        ...this.activeFilter,
      })
    },
    onSelectPractice(data) {
      const { id, value } = data
      if (value) {
        if (!this.selectedPractices.includes(id)) {
          this.selectedPractices.push(id)
        }
      } else {
        const index = this.selectedPractices.indexOf(id)
        if (index > -1) {
          this.selectedPractices.splice(index, 1)
        }
      }
    },
    generateCompaniesReport() {
      if (this.selectedPractices.length === 0) {
        return
      }

      this.generateReport({ companies: this.selectedPractices })
      this.selectedPractices = []
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.practices {
  padding: 2 * $gutter;

  &-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 16px;
  }
}
</style>
